import * as React from 'react'

import { useDelayedCallback } from '../state/DelayedCallback'

import { TimerApi, useTimer } from '../state/Timer'

/* TYPES */

export interface DelayedToggleConfig {
  afterDisable?: () => any
  afterEnable?: () => any
  beforeDisable?: (e: Event) => boolean | Promise<boolean>
  beforeEnable?: (e: Event) => boolean | Promise<boolean>
  disableDelay?: number
  enableDelay?: number
  isEnabled?: boolean
  onDisable: () => any
  onEnable: () => any
  timer?: TimerApi
}

export interface DelayedToggleProps extends DelayedToggleConfig {
  children: React.ReactNode
}

interface DelayedToggleRenderProps extends DelayedToggleConfig {
  children: React.ReactElement | ((props: Api) => React.ReactElement)
}

export interface Api {
  disable: (event: any) => any
  enable: (event: any) => any
  toggle: (event: any) => any
}

/* HOOK */

export const useDelayedToggle: (config: DelayedToggleConfig) => Api = ({
  afterDisable = () => true,
  afterEnable = () => true,
  beforeDisable = () => true,
  beforeEnable = () => true,
  disableDelay = 0,
  enableDelay = 0,
  isEnabled = false,
  onDisable,
  onEnable,
  timer = useTimer({}),
}) => {
  const enable = useDelayedCallback({
    after: () => {
      onEnable()
      afterEnable()
    },
    before: beforeEnable,
    delay: enableDelay,
    timer,
  })

  const disable = useDelayedCallback({
    after: () => {
      onDisable()
      afterDisable()
    },
    before: beforeDisable,
    delay: disableDelay,
    timer,
  })

  return {
    disable,
    enable,
    toggle: isEnabled ? disable : enable,
  }
}

/* DECORATOR */

export const withDelayedToggle = <T extends {}>(
  config: DelayedToggleConfig
) => (Component: React.ComponentType<T>) => (
  props: T & { DelayedToggle: DelayedToggleConfig }
) => {
  return (
    <Component
      {...props}
      delayedToggle={useDelayedToggle({
        ...config,
        ...props.DelayedToggle,
      })}
    />
  )
}

/* RENDER PROP */

export const DelayedToggle = React.forwardRef<
  HTMLElement,
  DelayedToggleRenderProps
>((props, ref) => {
  const { children, ...config } = props
  const toggle = useDelayedToggle(config)

  const element =
    typeof children === 'function'
      ? (children as ((props: Api) => React.ReactElement))(toggle)
      : (children as React.ReactElement)

  return element && React.cloneElement(element, { ref })
})

/* CONTEXT */

const noop: (method: string) => (event?: any) => any = method => () =>
  console.warn(
    `Could not call \`${method}\`, please wrap your component in an \`<DelayedToggleProvider>\``
  )

export const initDelayedToggle: Api = {
  disable: noop('disable'),
  enable: noop('enable'),
  toggle: noop('toggle'),
}

export const DelayedToggleContext = React.createContext<Api>(initDelayedToggle)

export const DelayedToggleProvider = ({
  children,
  ...props
}: DelayedToggleProps) => (
  <DelayedToggleContext.Provider value={useDelayedToggle(props)}>
    {children}
  </DelayedToggleContext.Provider>
)

export const consumeDelayedToggle = () => React.useContext(DelayedToggleContext)
